import React, { useState, useEffect, useRef } from "react";
import { appConfirmMessage } from '../../../utils/appConfirm';
import "../styles/AdminWorkForce.css";
import { graphqlRequest } from "../../../utils/graphqlRequest";
import { toast } from "react-toastify";
import MemberDetailsModal from "./MemberDetailsModal";
import EditMemberModal from "./EditMemberModal";
import { useAdminListOverlay } from "../../../utils/useAdminPageSearch";

const ADMIN_ROLES = ["ADMIN", "SUPER_ADMIN", "BRANCH_SUPER_ADMIN"];
const PAGE_SIZE = 10;

const formatMember = (user, index) => ({
  id: user.employeeCode || `ADM${String(index + 1).padStart(3, "0")}`,
  dbId: user.id,
  name: `${user.firstName} ${user.lastName}`,
  title: "Admin",
  email: user.email,
  contact: user.contact || "N/A",
});

export default function AdminWorkForce({ refreshKey = 0 }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [liveChatEnabled, setLiveChatEnabled] = useState(false);
  const [liveChatSupporterId, setLiveChatSupporterId] = useState("");
  const [isSavingLiveChat, setIsSavingLiveChat] = useState(false);
  const [fullUserForDetails, setFullUserForDetails] = useState(null);
  const overlay = useAdminListOverlay(members, { idKey: "dbId" });
  const detailsMember = overlay.viewing;
  const editMember = overlay.editing;

  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);

  const fetchPage = async (pageNum, isInitial = false) => {
    if (!isInitial && loadingMoreRef.current) return;
    if (!isInitial) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    try {
      const query = `
        query GetAllUser($page: Int, $limit: Int, $roles: [String!]) {
          getAllUser(page: $page, limit: $limit, roles: $roles) {
            data {
              id
              firstName
              lastName
              email
              contact
              roles
              employeeCode
            }
            total
            page
            limit
            totalPages
          }
          getMyProfile {
            company {
              liveChatEnabled
              liveChatSupporterId
            }
          }
        }
      `;

      const data = await graphqlRequest(query, {
        page: pageNum,
        limit: PAGE_SIZE,
        roles: ADMIN_ROLES,
      });

      const result = data?.getAllUser;
      const users = result?.data || [];
      const formatted = users.map(formatMember);

      if (isInitial) {
        const profileCompany = data?.getMyProfile?.company || {};
        setLiveChatEnabled(Boolean(profileCompany?.liveChatEnabled));
        setLiveChatSupporterId(profileCompany?.liveChatSupporterId || "");
        setMembers(formatted);
      } else {
        setMembers((prev) => {
          const existingIds = new Set(prev.map((m) => m.dbId));
          const newMembers = formatted.filter((m) => !existingIds.has(m.dbId));
          return [...prev, ...newMembers];
        });
      }

      pageRef.current = result?.page ?? pageNum;
      setHasMore((result?.page ?? pageNum) < (result?.totalPages ?? 1));
    } catch (err) {
      console.error("Error fetching admin members:", err);
      const msg = err?.message || "Failed to fetch members.";
      if (
        msg.includes("Unauthorized") ||
        msg.includes("token") ||
        msg.includes("authentication")
      ) {
        toast.error("Please log in again to access this page");
        setTimeout(() => { window.location.href = "/login"; }, 2000);
      } else if (isInitial) {
        toast.error(msg);
      }
      if (isInitial) setMembers([]);
    } finally {
      if (isInitial) setLoading(false);
      else {
        setLoadingMore(false);
      }
    }
  };

  // Initial load / refresh
  useEffect(() => {
    setLoading(true);
    setMembers([]);
    setHasMore(false);
    pageRef.current = 1;
    fetchPage(1, true);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll — fire when sentinel enters viewport
  useEffect(() => {
    loadingMoreRef.current = false;
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) {
          fetchPage(pageRef.current + 1);
        }
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, members.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveLiveChatSupporter = async () => {
    if (liveChatEnabled && !liveChatSupporterId) {
      toast.error("Please select a live chat supporter before enabling live chat.");
      return;
    }
    try {
      setIsSavingLiveChat(true);
      const mutation = `
        mutation UpdateCompany($updateCompanyInput: UpdateCompanyInput!) {
          updateCompany(updateCompanyInput: $updateCompanyInput) {
            id liveChatEnabled liveChatSupporterId
          }
        }
      `;
      const result = await graphqlRequest(mutation, {
        updateCompanyInput: {
          liveChatEnabled,
          liveChatSupporterId: liveChatSupporterId.trim() || null,
        },
      });
      const company = result?.updateCompany;
      setLiveChatEnabled(Boolean(company?.liveChatEnabled));
      setLiveChatSupporterId(company?.liveChatSupporterId || "");
      toast.success("Live chat supporter updated.");
    } catch (error) {
      toast.error(error?.message || "Failed to update live chat supporter");
    } finally {
      setIsSavingLiveChat(false);
    }
  };

  const handleDelete = async (index) => {
    if (!await appConfirmMessage("Are you sure you want to delete this member?")) return;
    try {
      const member = members[index];
      await graphqlRequest(
        `mutation RemoveUser($id: String!) { removeUser(id: $id) { id } }`,
        { id: member.dbId }
      );
      setMembers((prev) => prev.filter((_, i) => i !== index));
      toast.success("Member deleted successfully!");
    } catch (err) {
      toast.error(
        err?.response?.data?.errors?.[0]?.message ||
          err?.message ||
          "Failed to delete member."
      );
    }
  };

  const handleEdit = (index) => overlay.openEdit(members[index]);

  const handleViewDetails = (index) => overlay.openView(members[index]);

  useEffect(() => {
    const member = overlay.viewing;
    if (!member?.dbId) {
      setFullUserForDetails(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await graphqlRequest(
          `query GetSingleUser($GetUserInput: GetUserInput!) {
            getSingleUser(GetUserInput: $GetUserInput) {
              id firstName lastName email contact DOB address gender roles
              taxResidency bsb accountNumber taxFileNumber abnNumber
              superannuationAccountNumber emergencyContactName emergencyContactNumber
              claimTaxFreeThreshold jobTitle perHourRate superAnnuation salaryTerm
              typeOfWork paydayDay paydayTime joiningDate fundName spin fundABN
              fundTelephoneNumber memberNumber
            }
          }`,
          { GetUserInput: { id: member.dbId } }
        );
        if (!cancelled && data?.getSingleUser) setFullUserForDetails(data.getSingleUser);
      } catch (err) {
        console.error("Error fetching member details:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [overlay.viewing]);

  const refreshMembers = () => {
    setMembers([]);
    setHasMore(false);
    pageRef.current = 1;
    fetchPage(1, true);
  };

  return (
    <div className="Admin-WorkForce-Content">
      <h2 className="Admin-WorkForce-Heading">Admin Workforce</h2>
      <div className="Admin-WorkForce-LiveChatCard">
        <div className="Admin-WorkForce-LiveChatHeader">
          <h3>Live Chat Support</h3>
          <label className="Admin-WorkForce-ToggleWrap">
            <input
              type="checkbox"
              checked={liveChatEnabled}
              onChange={(e) => setLiveChatEnabled(e.target.checked)}
            />
            <span>{liveChatEnabled ? "Enabled" : "Disabled"}</span>
          </label>
        </div>
        <p className="Admin-WorkForce-LiveChatHelp">
          Choose one admin as live chat supporter. Employees can contact this user from Help &amp; Support.
        </p>
        <div className="Admin-WorkForce-LiveChatControls">
          <select
            value={liveChatSupporterId}
            onChange={(e) => {
              setLiveChatSupporterId(e.target.value);
              if (e.target.value) setLiveChatEnabled(true);
            }}
            disabled={loading}
          >
            <option value="">Select live chat supporter</option>
            {members.map((m) => (
              <option key={m.dbId} value={m.dbId}>
                {m.name} ({m.email})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="Admin-WorkForce-LiveChatSave"
            onClick={handleSaveLiveChatSupporter}
            disabled={isSavingLiveChat || loading}
          >
            {isSavingLiveChat ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
      <div className="Admin-WorkForce-TableWrapper">
        <table className="Admin-WorkForce-Table">
          <thead>
            <tr>
              <th>Employee id</th>
              <th>Name</th>
              <th>Title</th>
              <th>Email</th>
              <th>Contact Number</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                  Loading members...
                </td>
              </tr>
            ) : members.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                  No admin members found
                </td>
              </tr>
            ) : (
              members.map((member, index) => (
                <tr key={member.dbId}>
                  <td>{member.id}</td>
                  <td>{member.name}</td>
                  <td>{member.title}</td>
                  <td>{member.email}</td>
                  <td>{member.contact}</td>
                  <td>
                    <button className="Admin-WorkForce-ViewDetails" onClick={() => handleViewDetails(index)}>
                      View Details
                    </button>
                    <button className="Admin-WorkForce-Edit" onClick={() => handleEdit(index)}>
                      Edit
                    </button>
                    <button className="Admin-WorkForce-Delete" onClick={() => handleDelete(index)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Sentinel — sits below the table; viewport scroll triggers next page fetch */}
      {!loading && hasMore && (
        <div ref={sentinelRef} style={{ height: "1px" }} />
      )}
      {loadingMore && (
        <div style={{ textAlign: "center", padding: "10px", color: "#9ca3af", fontSize: "13px" }}>
          Loading more...
        </div>
      )}
      {detailsMember && (
        <MemberDetailsModal
          member={detailsMember}
          fullUser={fullUserForDetails}
          onClose={() => { overlay.closeOverlay(); setFullUserForDetails(null); }}
        />
      )}
      {editMember && (
        <EditMemberModal
          member={editMember}
          onClose={() => overlay.closeOverlay()}
          onSaved={refreshMembers}
        />
      )}
    </div>
  );
}
